import type { Entity, Link, LinkType } from '@/types/entity';
import type { Project, ProjectMeta } from '@/types/project';
import type { ProposalBundleRecord, ProposalDeltaOp } from '@/db/codexDB';
import type { ProposalPatchOp } from '@/lib/schemas/proposal.schema';
import { getAssertionCandidates, setAssertionStatus } from './canonBundle';
import { appendDraftBody } from '@/lib/prose';

/**
 * 커널 제안 수락 = Δ를 Dexie 정본에 적용(K1) — **유일한 커밋 경로의 실행부**(작가의 수락 클릭에서만 호출).
 * fact의 정본 위치 두 곳을 모두 다룬다:
 *  ① 작가 선언 — project.meta.engine.timeScopes[scope].facts
 *  ② 비트 유래 — accepted assertion의 defines_fact (빼기 = 그 전제를 rejected로, 주권 경계 동일)
 * 주입형 ctx — store 비의존(테스트 가능). 반환 = 적용 노트(감사, proposalBundles.appliedNotes로).
 */
export interface ApplyCtx {
  project: Project | null;
  entities: Record<string, Entity>;
  patchProjectMeta: (patch: Partial<ProjectMeta>) => void;
  patchEntity: (id: string, patch: Partial<Entity>) => void;
  /** K5-d 패치 집행용(선택) — 관계·산문 필드는 전용 액션으로(멘션 흡수 등 기존 배관을 태운다) */
  links?: Record<string, Link>;
  patchField?: (id: string, key: string, value: unknown) => void;
  addLink?: (input: { from: string; to: string; type: LinkType; label?: string; directed?: boolean }) => string;
  removeLink?: (id: string) => void;
}

function isRecord(v: unknown): v is Record<string, unknown> {
  return Boolean(v) && typeof v === 'object' && !Array.isArray(v);
}

export function applyProposalDelta(delta: ProposalDeltaOp[], ctx: ApplyCtx): string[] {
  const notes: string[] = [];
  if (!ctx.project) return ['! 프로젝트 없음 — 적용 불가'];

  const engineRaw = isRecord(ctx.project.meta.engine) ? ctx.project.meta.engine : {};
  const tsRaw = isRecord(engineRaw.timeScopes) ? engineRaw.timeScopes : {};
  // 작업 사본 — scope 항목의 기존 키(lawless 등)는 보존하고 facts만 다룬다.
  const nextTs: Record<string, Record<string, unknown>> = {};
  for (const [k, v] of Object.entries(tsRaw)) nextTs[k] = isRecord(v) ? { ...v } : {};
  let tsChanged = false;

  const factsOf = (scope: string): string[] => {
    const cur = nextTs[scope];
    return cur && Array.isArray(cur.facts) ? (cur.facts as unknown[]).filter((f): f is string => typeof f === 'string') : [];
  };
  const setFacts = (scope: string, facts: string[]) => {
    nextTs[scope] = { ...(nextTs[scope] ?? {}), facts };
    tsChanged = true;
  };

  const addFact = (scope: string, fact: string) => {
    const facts = factsOf(scope);
    if (facts.includes(fact)) {
      notes.push(`= [${scope}] «${fact}» 이미 있음`);
      return;
    }
    setFacts(scope, [...facts, fact]);
    notes.push(`+ [${scope}] «${fact}»`);
  };

  const removeFact = (scope: string, fact: string) => {
    const facts = factsOf(scope);
    if (facts.includes(fact)) {
      setFacts(scope, facts.filter((f) => f !== fact));
      notes.push(`- [${scope}] «${fact}» (프로젝트 선언에서)`);
      return;
    }
    // 비트 유래(defines_fact) 폴백 — 해당 accepted 전제를 rejected로(다음 번들에서 fact 소멸).
    for (const entity of Object.values(ctx.entities)) {
      if (entity.type !== 'beat' || entity.deletedAt) continue;
      const scopeOk = String(entity.fields.col ?? '') === scope;
      const cand = getAssertionCandidates(entity).find(
        (c) => c.status === 'accepted' && c.defines_fact === fact && scopeOk,
      );
      if (cand) {
        const next = setAssertionStatus(entity, cand.id, 'rejected');
        ctx.patchEntity(entity.id, { meta: next.meta });
        notes.push(`- [${scope}] «${fact}» (비트 ${entity.id}의 defines_fact 전제 빼기)`);
        return;
      }
    }
    notes.push(`! [${scope}] «${fact}» — 원천을 못 찾음(건너뜀)`);
  };

  // K5-c: 거시 Δ — 자원 붕괴/복원·제도 상태는 해당 엔티티의 meta.engine에 적용(투영이 그대로 집어든다).
  const patchEngineMeta = (entityId: string, kind: string, mutate: (eng: Record<string, unknown>) => string | null) => {
    const entity = ctx.entities[entityId];
    if (!entity || entity.deletedAt) {
      notes.push(`! ${kind} «${entityId}» 엔티티 없음(건너뜀)`);
      return;
    }
    const meta = isRecord(entity.meta) ? entity.meta : {};
    const eng = isRecord(meta.engine) ? { ...meta.engine } : {};
    const note = mutate(eng);
    if (note === null) return;
    ctx.patchEntity(entity.id, { meta: { ...meta, engine: eng } });
    notes.push(note);
  };

  for (const d of delta) {
    if (d.op === 'set_fact') addFact(d.scope, d.fact);
    else if (d.op === 'clear_fact') removeFact(d.scope, d.fact);
    else if (d.op === 'rename_fact') {
      removeFact(d.scope, d.from);
      addFact(d.scope, d.to);
    } else if (d.op === 'collapse_resource' || d.op === 'uncollapse_resource') {
      const on = d.op === 'collapse_resource';
      patchEngineMeta(d.resource, '자원', (eng) => {
        const cur = Array.isArray(eng.collapses_in)
          ? (eng.collapses_in as unknown[]).filter((v): v is string => typeof v === 'string')
          : [];
        if (on === cur.includes(d.scope)) {
          notes.push(`= 자원 «${d.resource}» ${d.scope} 붕괴 상태 이미 동일`);
          return null;
        }
        eng.collapses_in = on ? [...cur, d.scope] : cur.filter((v) => v !== d.scope);
        return on ? `⤓ 자원 «${d.resource}» — ${d.scope}에서 붕괴` : `⤒ 자원 «${d.resource}» — ${d.scope} 붕괴 취소`;
      });
    } else if (d.op === 'set_institution_status') {
      patchEngineMeta(d.institution, '제도/세력', (eng) => {
        eng.status = d.status;
        return `◌ 제도 «${d.institution}» status=${d.status}`;
      });
    }
  }

  if (tsChanged) {
    ctx.patchProjectMeta({ engine: { ...engineRaw, timeScopes: nextTs } });
  }
  return notes;
}

/**
 * 제네릭 패치 수락(K5-d) = 엔티티 카드·관계·프로젝트 메타의 구체 수정을 기존 store 액션으로 집행.
 * 전부 작가의 수락 클릭에서만 — 누락 대상·중복·보호 키는 조용히 실패하지 않고 노트로 남긴다.
 */
export function applyProposalPatches(patches: ProposalPatchOp[], ctx: ApplyCtx): string[] {
  const notes: string[] = [];
  const entityOf = (id: string, what: string): Entity | null => {
    const e = ctx.entities[id];
    if (!e || e.deletedAt) {
      notes.push(`! ${what} «${id}» 엔티티 없음(건너뜀)`);
      return null;
    }
    return e;
  };
  const aliveLinks = () => Object.values(ctx.links ?? {}).filter((l) => !l.deletedAt);

  for (const p of patches) {
    if (p.kind === 'entity_field') {
      const e = entityOf(p.entity, '대상');
      if (!e) continue;
      if (ctx.patchField) ctx.patchField(e.id, p.field, p.value);
      else ctx.patchEntity(e.id, { fields: { ...e.fields, [p.field]: p.value } });
      notes.push(`✎ «${e.name}».${p.field} 수정`);
    } else if (p.kind === 'entity_name') {
      const e = entityOf(p.entity, '대상');
      if (!e) continue;
      ctx.patchEntity(e.id, { name: p.name });
      notes.push(`✎ «${e.name}» → 이름 «${p.name}»`);
    } else if (p.kind === 'entity_summary') {
      const e = entityOf(p.entity, '대상');
      if (!e) continue;
      ctx.patchEntity(e.id, { summary: p.summary });
      notes.push(`✎ «${e.name}» 요약 수정`);
    } else if (p.kind === 'entity_tags') {
      const e = entityOf(p.entity, '대상');
      if (!e) continue;
      ctx.patchEntity(e.id, { tags: p.tags });
      notes.push(`✎ «${e.name}» 태그 ${p.tags.length}개`);
    } else if (p.kind === 'add_link') {
      if (!ctx.addLink) {
        notes.push('! 관계 추가 액션 없음(컨텍스트 미배선)');
        continue;
      }
      const from = entityOf(p.from, '관계 from');
      const to = entityOf(p.to, '관계 to');
      if (!from || !to) continue;
      const dup = aliveLinks().some(
        (l) => l.type === p.type && ((l.from === from.id && l.to === to.id) || (!l.directed && l.from === to.id && l.to === from.id)),
      );
      if (dup) {
        notes.push(`= 관계 «${from.name}»—${p.type}—«${to.name}» 이미 있음`);
        continue;
      }
      ctx.addLink({ from: from.id, to: to.id, type: p.type as LinkType, ...(p.label ? { label: p.label } : {}), ...(p.directed !== undefined ? { directed: p.directed } : {}) });
      notes.push(`+ 관계 «${from.name}» —${p.type}— «${to.name}»`);
    } else if (p.kind === 'remove_link') {
      if (!ctx.removeLink) {
        notes.push('! 관계 제거 액션 없음(컨텍스트 미배선)');
        continue;
      }
      const hit = aliveLinks().find(
        (l) => l.type === p.type && ((l.from === p.from && l.to === p.to) || (l.from === p.to && l.to === p.from)),
      );
      if (!hit) {
        notes.push(`! 관계 ${p.from}—${p.type}—${p.to} 를 못 찾음(건너뜀)`);
        continue;
      }
      ctx.removeLink(hit.id);
      notes.push(`− 관계 ${p.from} —${p.type}— ${p.to}`);
    } else if (p.kind === 'project_meta') {
      if (p.key === 'engine') {
        notes.push('! meta.engine은 패치 금지(구조 정본 — 전용 Δ 경로로만)');
        continue;
      }
      ctx.patchProjectMeta({ [p.key]: p.value } as Partial<ProjectMeta>);
      notes.push(`✎ 프로젝트.${p.key} 수정`);
    }
  }
  return notes;
}

/**
 * 씬 제안 수락(K3) = 비트 초안(bodyDraft)에 비파괴 이어쓰기 — bodyDraft는 작가의 초안 공간(canon 아님),
 * canon 입력은 이후 작가가 비추기/수락으로. 봉인 1차 게이트는 사이드카가 이미 통과시킨 것만 도착.
 */
export function applySceneProposal(scene: NonNullable<ProposalBundleRecord['scene']>, ctx: ApplyCtx): string[] {
  const beat = ctx.entities[scene.beatId];
  if (!beat || beat.type !== 'beat' || beat.deletedAt) {
    return [`! 비트 «${scene.beatId}» 없음 — 반영 건너뜀(씬 캔버스에서 수동 반영 가능)`];
  }
  ctx.patchEntity(beat.id, {
    fields: { ...beat.fields, bodyDraft: appendDraftBody(String(beat.fields.bodyDraft ?? ''), scene.prose) },
  });
  return [`씬 ${scene.prose.length}자 → 비트 «${beat.name}» 초안에 이어쓰기`];
}
